import { SEVERITY } from './issue';

/**
 * Presentation tone shared by chips, issue rows, meters and toasts, so a
 * warning looks the same everywhere. Severity values are reused directly: an
 * issue's severity *is* a tone.
 */
export const TONE = {
  NEUTRAL: 'neutral',
  SUCCESS: 'success',
  ERROR: SEVERITY.ERROR,
  WARNING: SEVERITY.WARNING,
  INFO: SEVERITY.INFO,
} as const;

export type Tone = (typeof TONE)[keyof typeof TONE];

/** Badge and chip surface. */
export const TONE_CHIP = {
  [TONE.NEUTRAL]: 'bg-[#F5F5F5] text-[#666666]',
  [TONE.SUCCESS]: 'bg-[#4CAF50]/12 text-[#2E7D32]',
  [TONE.ERROR]: 'bg-[#F44336]/12 text-[#C62828]',
  [TONE.WARNING]: 'bg-[#FF9800]/14 text-[#B45309]',
  [TONE.INFO]: 'bg-[#2196F3]/12 text-[#1565C0]',
} as const satisfies Record<Tone, string>;

/** Text-only emphasis. */
export const TONE_TEXT = {
  [TONE.NEUTRAL]: 'text-[#666666]',
  [TONE.SUCCESS]: 'text-[#2E7D32]',
  [TONE.ERROR]: 'text-[#C62828]',
  [TONE.WARNING]: 'text-[#B45309]',
  [TONE.INFO]: 'text-[#1565C0]',
} as const satisfies Record<Tone, string>;

/** Solid dot or meter bar. */
export const TONE_DOT = {
  [TONE.NEUTRAL]: 'bg-[#999999]',
  [TONE.SUCCESS]: 'bg-[#4CAF50]',
  [TONE.ERROR]: 'bg-[#F44336]',
  [TONE.WARNING]: 'bg-[#FF9800]',
  [TONE.INFO]: 'bg-[#2196F3]',
} as const satisfies Record<Tone, string>;

/** Left edge of an issue row. */
export const TONE_BORDER = {
  [TONE.NEUTRAL]: 'border-l-[#E0E0E0]',
  [TONE.SUCCESS]: 'border-l-[#4CAF50]',
  [TONE.ERROR]: 'border-l-[#F44336]',
  [TONE.WARNING]: 'border-l-[#FF9800]',
  [TONE.INFO]: 'border-l-[#2196F3]',
} as const satisfies Record<Tone, string>;

export const TOAST_DURATION_MS = 3_500;

/** Older toasts fall off the stack, so a burst cannot cover the screen. */
export const TOAST_MAX_VISIBLE = 3;
